import {FC, useEffect, useRef} from 'react';
import {
  Box,
  IconButton,
  Paper,
  Popper,
  PopperPlacementType,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {t} from 'i18next';
import './SummaryViewerPopup.scss';

type SummaryViewerPopupProps = {
  anchorElement: HTMLElement | null;
  docId: string;
  summary: string;
  onClose: () => void;
};

// Matches `.summary-viewer-popup` width (33.75rem) in the SCSS.
const POPUP_WIDTH = 540;

// Gap in px between the anchor and the popup.
const ANCHOR_GAP = 8;

const SummaryViewerPopup: FC<SummaryViewerPopupProps> = ({
  anchorElement,
  docId,
  summary,
  onClose,
}) => {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Close on outside click without swallowing the event.
  useEffect(() => {
    if (!anchorElement) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !popupRef.current?.contains(target) &&
        !anchorElement.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorElement, onClose]);

  // Close the popup on Escape.
  useEffect(() => {
    if (!anchorElement) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc, true);
    return () => {
      window.removeEventListener('keydown', handleEsc, true);
    };
  }, [anchorElement, onClose]);

  // Focus the popup
  useEffect(() => {
    if (!anchorElement) return;
    // On open, focus the popup container (not in the tab order, so the first
    // Tab lands on the close button); on close, return focus to the anchor.
    const anchorToRestore = anchorElement;
    const frameId = requestAnimationFrame(() => {
      popupRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(frameId);
      anchorToRestore.focus();
    };
  }, [anchorElement]);

  // Side of the anchor with more horizontal space.
  const getPlacement = (): PopperPlacementType => {
    if (!anchorElement) return 'right-start';
    const rect = anchorElement.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;
    return spaceRight >= spaceLeft ? 'right-start' : 'left-start';
  };

  // True when neither side can fit the popup.
  const shouldCenter = (): boolean => {
    if (!anchorElement) return false;
    const rect = anchorElement.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;
    return spaceRight < POPUP_WIDTH && spaceLeft < POPUP_WIDTH;
  };

  const isCentered = shouldCenter();
  // Stable base placement when centering keeps the offset math predictable.
  const placement: PopperPlacementType = isCentered
    ? 'right-start'
    : getPlacement();

  const popperModifiers = [
    {
      name: 'flip',
      enabled: false,
    },
    {
      name: 'offset',
      options: {
        offset: ({popper}: {popper: {height: number; width: number}}) => {
          if (!anchorElement) return [0, ANCHOR_GAP];
          const rect = anchorElement.getBoundingClientRect();
          // offset is [crossAxis (vertical), mainAxis (horizontal)].
          const desiredTop = (window.innerHeight - popper.height) / 2;
          // Center in the viewport when no side fits.
          if (isCentered) {
            const desiredLeft = (window.innerWidth - popper.width) / 2;
            return [desiredTop - rect.top, desiredLeft - rect.right];
          }
          // Otherwise keep it vertically centered beside the anchor.
          return [desiredTop - rect.top, ANCHOR_GAP];
        },
      },
    },
    {
      name: 'preventOverflow',
      enabled: false,
    },
  ];

  // Trap Tab focus within the popup so it cycles between the first and last
  // focusable elements instead of escaping to the page behind it.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const root = popupRef.current;
    if (!root) return;

    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>(focusableSelector)
    ).filter((el) => el.offsetParent !== null);
    if (nodes.length === 0) return;

    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (active === first || !root.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <Popper
      data-testid="summary-viewer-popup"
      open={Boolean(anchorElement)}
      anchorEl={anchorElement}
      placement={placement}
      modifiers={popperModifiers}
      className="ai-summary-viewer-popup"
    >
      <Paper
        className="summary-viewer-popup"
        ref={popupRef}
        data-testid="summary-viewer-popup-paper"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <Box className="popup-header" data-testid="summary-viewer-popup-header">
          <Box
            className="popup-header-text"
            data-testid="summary-viewer-popup-header-text"
          >
            <Typography
              className="header-title typo-text headline3_size"
              data-testid="summary-viewer-popup-title"
            >
              {t('docList.summaryViewwer.popupTitle')}
            </Typography>
            <Typography
              className="header-id typo-text label2_size"
              data-testid="summary-viewer-popup-doc-id"
            >
              {t('docList.summaryViewwer.docIdLabel')}: {docId}
            </Typography>
          </Box>
          <Box>
            <IconButton
              data-testid="summary-viewer-popup-close-button"
              aria-label={t('common.close')}
              size="small"
              onClick={onClose}
              className="popup-close"
              ref={closeButtonRef}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box className="popup-body" data-testid="summary-viewer-popup-body">
          <Typography
            className="summary-text typo-text body2_size"
            data-testid="summary-viewer-popup-summary-text"
          >
            {summary}
          </Typography>
        </Box>
      </Paper>
    </Popper>
  );
};

export default SummaryViewerPopup;
